import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import './Admin.css';

const IMG_BASE = 'https://runarounddogtown.com/rescue-api';

// ════════════════════════════════════════════════════════════
//  MEMORIAL
// ════════════════════════════════════════════════════════════
export function AdminMemorial() {
  const [dogs,    setDogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [current, setCurrent] = useState(null);
  const [form,    setForm]    = useState({ name:'', breed:'', story:'', date_passed:'', adopted_by:'', sort_order:0 });
  const [photo,   setPhoto]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.getMemorial().then(setDogs).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm({ name:'', breed:'', story:'', date_passed:'', adopted_by:'', sort_order:0 });
    setCurrent(null); setPhoto(null); setError(''); setModal('form');
  };

  const openEdit = d => {
    setCurrent(d);
    setForm({ name:d.name||'', breed:d.breed||'', story:d.story||'', date_passed:d.date_passed||'', adopted_by:d.adopted_by||'', sort_order:d.sort_order||0 });
    setPhoto(null); setError(''); setModal('form');
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append('photo', photo);
      current ? await api.admin.updateMemorial(current.id, fd) : await api.admin.createMemorial(fd);
      setModal(null); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    try { await api.admin.deleteMemorial(id); setConfirmDelete(null); load(); }
    catch (err) { alert(err.message); }
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div><h1 className="admin-page__title">Memorial Wall</h1></div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Entry</button>
      </div>

      <div className="admin-section">
        {loading ? <div className="loading-center"><div className="spinner" /></div>
        : dogs.length === 0 ? (
          <div className="admin-empty"><div className="admin-empty__icon">🌹</div><h3>No memorial entries yet</h3></div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Photo</th><th>Name</th><th>Breed</th><th>Date Passed</th><th>Adopted By</th><th>Actions</th></tr></thead>
              <tbody>
                {dogs.map(d => (
                  <tr key={d.id}>
                    <td>{d.photo ? <img className="dog-thumb" src={`${IMG_BASE}${d.photo}`} alt={d.name} style={{ filter:'grayscale(30%)' }} /> : <div className="dog-thumb-placeholder">🌈</div>}</td>
                    <td><strong>{d.name}</strong></td>
                    <td>{d.breed || '—'}</td>
                    <td>{d.date_passed ? new Date(d.date_passed+'T12:00:00').toLocaleDateString() : '—'}</td>
                    <td>{d.adopted_by || '—'}</td>
                    <td>
                      {confirmDelete === d.id ? (
                        <div className="confirm-delete">Sure? <button className="btn-icon btn-icon--danger" onClick={() => handleDelete(d.id)}>Yes</button><button className="btn-icon" onClick={() => setConfirmDelete(null)}>No</button></div>
                      ) : (
                        <div className="table-actions">
                          <button className="btn-icon" onClick={() => openEdit(d)}>✏️ Edit</button>
                          <button className="btn-icon btn-icon--danger" onClick={() => setConfirmDelete(d.id)}>🗑️</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal === 'form' && (
        <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="admin-modal">
            <div className="admin-modal__header">
              <h2>{current ? `Edit: ${current.name}` : 'Add Memorial Entry'}</h2>
              <button className="admin-modal__close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="admin-modal__body">
              {error && <div className="alert alert-error">{error}</div>}
              <div className="form-grid-2">
                <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} autoFocus /></div>
                <div className="form-group"><label className="form-label">Breed</label><input className="form-control" value={form.breed} onChange={e => f('breed', e.target.value)} /></div>
              </div>
              <div className="form-grid-2">
                <div className="form-group"><label className="form-label">Date Passed</label><input className="form-control" type="date" value={form.date_passed} onChange={e => f('date_passed', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Adopted By (family name)</label><input className="form-control" value={form.adopted_by} onChange={e => f('adopted_by', e.target.value)} placeholder="e.g. The Johnson Family" /></div>
              </div>
              <div className="form-group"><label className="form-label">Story / Memorial Message</label><textarea className="form-control" rows={4} value={form.story} onChange={e => f('story', e.target.value)} /></div>
              <div className="form-group">
                <label className="form-label">Photo</label>
                {current?.photo && !photo && <img src={`${IMG_BASE}${current.photo}`} alt="" style={{ width:80, height:80, objectFit:'cover', borderRadius:'var(--radius)', marginBottom:8, filter:'grayscale(30%)' }} />}
                <label className="photo-upload-area"><input type="file" accept="image/*" onChange={e => setPhoto(e.target.files[0])} />{photo ? `✓ ${photo.name}` : 'Click to upload photo'}</label>
              </div>
            </div>
            <div className="admin-modal__footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : current ? 'Save' : 'Add Entry'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  USERS
// ════════════════════════════════════════════════════════════
export function AdminUsers() {
  const { user: me } = useAuth();
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [current, setCurrent] = useState(null);
  const [form,    setForm]    = useState({ name:'', email:'', password:'', role:'admin' });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.admin.getUsers().then(setUsers).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm({ name:'', email:'', password:'', role:'admin' }); setCurrent(null); setError(''); setModal('form'); };
  const openEdit = u  => { setCurrent(u); setForm({ name:u.name, email:u.email, password:'', role:u.role }); setError(''); setModal('form'); };
  const f = (k, v)    => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.email) { setError('Name and email are required'); return; }
    if (!current && !form.password) { setError('Password is required for new users'); return; }
    setSaving(true); setError('');
    try {
      current ? await api.admin.updateUser(current.id, form) : await api.admin.createUser(form);
      setModal(null); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    try { await api.admin.deleteUser(id); setConfirmDelete(null); load(); }
    catch (err) { alert(err.message); }
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div><h1 className="admin-page__title">Users</h1><p className="admin-page__subtitle">Admin accounts</p></div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add User</button>
      </div>

      <div className="admin-section">
        {loading ? <div className="loading-center"><div className="spinner" /></div>
        : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td><strong>{u.name}</strong>{u.id === me?.id && <span className="badge badge-available" style={{marginLeft:8}}>You</span>}</td>
                    <td>{u.email}</td>
                    <td><span className={`badge ${u.role === 'super_admin' ? 'badge-adopted' : 'badge-pending'}`}>{u.role?.replace('_',' ')}</span></td>
                    <td>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td>
                      {u.id === me?.id ? <em style={{ fontSize:'0.8rem', color:'var(--text-light)' }}>Your account</em>
                      : confirmDelete === u.id ? (
                        <div className="confirm-delete">Sure? <button className="btn-icon btn-icon--danger" onClick={() => handleDelete(u.id)}>Yes</button><button className="btn-icon" onClick={() => setConfirmDelete(null)}>No</button></div>
                      ) : (
                        <div className="table-actions">
                          <button className="btn-icon" onClick={() => openEdit(u)}>✏️ Edit</button>
                          <button className="btn-icon btn-icon--danger" onClick={() => setConfirmDelete(u.id)}>🗑️</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal === 'form' && (
        <div className="admin-modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="admin-modal">
            <div className="admin-modal__header">
              <h2>{current ? 'Edit User' : 'Add New User'}</h2>
              <button className="admin-modal__close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="admin-modal__body">
              {error && <div className="alert alert-error">{error}</div>}
              <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} autoFocus /></div>
              <div className="form-group"><label className="form-label">Email *</label><input className="form-control" type="email" value={form.email} onChange={e => f('email', e.target.value)} /></div>
              <div className="form-group">
                <label className="form-label">{current ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
                <input className="form-control" type="password" value={form.password} onChange={e => f('password', e.target.value)} placeholder={current ? 'Leave blank to keep current' : 'Min 8 characters'} />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-control" value={form.role} onChange={e => f('role', e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
            </div>
            <div className="admin-modal__footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : current ? 'Save' : 'Create User'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  SEO SETTINGS
// ════════════════════════════════════════════════════════════
export function AdminSEO() {
  const [seo,     setSeo]     = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(null);
  const [saved,   setSaved]   = useState(null);

  useEffect(() => { api.admin.getSeo().then(setSeo).finally(() => setLoading(false)); }, []);

  const handleSave = async (slug, data) => {
    setSaving(slug);
    try {
      const fd = new FormData();
      fd.append('meta_title',       data.meta_title       || '');
      fd.append('meta_description', data.meta_description || '');
      await api.admin.updateSeo(slug, fd);
      setSaved(slug);
      setTimeout(() => setSaved(null), 2000);
    } catch (err) { alert(err.message); }
    finally { setSaving(null); }
  };

  const updateField = (slug, field, value) => {
    setSeo(prev => ({ ...prev, [slug]: { ...prev[slug], [field]: value } }));
  };

  const pages = ['home','dogs','events','about','donate','contact','memorial'];

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div><h1 className="admin-page__title">SEO Settings</h1><p className="admin-page__subtitle">Meta titles and descriptions for each page</p></div>
      </div>

      {loading ? <div className="loading-center"><div className="spinner" /></div>
      : (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {pages.map(slug => {
            const entry = seo[slug] || {};
            return (
              <div key={slug} className="admin-section">
                <div className="admin-section__header">
                  <h2 style={{ textTransform:'capitalize' }}>{slug}</h2>
                  {saved === slug && <span style={{ color:'green', fontFamily:'var(--font-ui)', fontSize:'0.85rem' }}>✓ Saved</span>}
                </div>
                <div style={{ padding:20 }}>
                  <div className="form-group">
                    <label className="form-label">Meta Title</label>
                    <input className="form-control" value={entry.meta_title || ''} onChange={e => updateField(slug, 'meta_title', e.target.value)} placeholder="Page title shown in browser tab and search results" />
                    <small style={{ fontFamily:'var(--font-ui)', fontSize:'0.75rem', color: (entry.meta_title||'').length > 60 ? 'var(--red)' : 'var(--text-light)' }}>
                      {(entry.meta_title||'').length}/60 characters
                    </small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Meta Description</label>
                    <textarea className="form-control" rows={2} value={entry.meta_description || ''} onChange={e => updateField(slug, 'meta_description', e.target.value)} placeholder="Brief description shown in search results" />
                    <small style={{ fontFamily:'var(--font-ui)', fontSize:'0.75rem', color: (entry.meta_description||'').length > 160 ? 'var(--red)' : 'var(--text-light)' }}>
                      {(entry.meta_description||'').length}/160 characters
                    </small>
                  </div>
                  <button className="btn btn-primary" style={{ padding:'8px 20px' }} onClick={() => handleSave(slug, entry)} disabled={saving === slug}>
                    {saving === slug ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  SITE SETTINGS
// ════════════════════════════════════════════════════════════
export function AdminSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [pwForm,   setPwForm]   = useState({ currentPassword:'', newPassword:'', confirmPassword:'' });
  const [pwError,  setPwError]  = useState('');
  const [pwSaved,  setPwSaved]  = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => { api.getSettings().then(setSettings).finally(() => setLoading(false)); }, []);

  const set = (k, v) => setSettings(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.admin.updateSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handlePwChange = async () => {
    setPwError('');
    if (pwForm.newPassword !== pwForm.confirmPassword) { setPwError('Passwords do not match'); return; }
    if (pwForm.newPassword.length < 8) { setPwError('Must be at least 8 characters'); return; }
    setPwSaving(true);
    try {
      await api.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwSaved(true);
      setPwForm({ currentPassword:'', newPassword:'', confirmPassword:'' });
      setTimeout(() => setPwSaved(false), 3000);
    } catch (err) { setPwError(err.message); }
    finally { setPwSaving(false); }
  };

  const field = (label, key, type='text', placeholder='') => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="form-control" type={type} value={settings[key] || ''} onChange={e => set(key, e.target.value)} placeholder={placeholder} />
    </div>
  );

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div><h1 className="admin-page__title">Site Settings</h1></div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : saved ? '✓ Saved!' : 'Save All Settings'}
        </button>
      </div>

      {loading ? <div className="loading-center"><div className="spinner" /></div>
      : (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, alignItems:'start' }}>
          <div>
            <div className="admin-section" style={{ marginBottom:24 }}>
              <div className="admin-section__header"><h2>Organization Info</h2></div>
              <div style={{ padding:20 }}>
                {field('Organization Name', 'org_name')}
                {field('Contact Email',     'org_email', 'email')}
                {field('Phone Number',      'org_phone', 'tel')}
                {field('Location',          'org_location')}
              </div>
            </div>

            <div className="admin-section">
              <div className="admin-section__header"><h2>Social & Links</h2></div>
              <div style={{ padding:20 }}>
                {field('Facebook URL',  'facebook_url',  'url', 'https://facebook.com/...')}
                {field('Instagram URL', 'instagram_url', 'url', 'https://instagram.com/...')}
                {field('Donation URL',  'donation_url',  'url', 'https://...')}
              </div>
            </div>
          </div>

          <div>
            <div className="admin-section" style={{ marginBottom:24 }}>
              <div className="admin-section__header"><h2>Homepage Content</h2></div>
              <div style={{ padding:20 }}>
                <div className="form-group">
                  <label className="form-label">Hero Headline</label>
                  <textarea className="form-control" rows={2} value={settings.home_hero_text || ''} onChange={e => set('home_hero_text', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">About Blurb (mission strip)</label>
                  <textarea className="form-control" rows={3} value={settings.home_about_blurb || ''} onChange={e => set('home_about_blurb', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="admin-section">
              <div className="admin-section__header"><h2>Change Your Password</h2></div>
              <div style={{ padding:20 }}>
                {pwError  && <div className="alert alert-error">{pwError}</div>}
                {pwSaved  && <div className="alert alert-success">Password changed successfully!</div>}
                <div className="form-group">
                  <label className="form-label">Current Password</label>
                  <input className="form-control" type="password" value={pwForm.currentPassword} onChange={e => setPwForm(p => ({ ...p, currentPassword: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input className="form-control" type="password" value={pwForm.newPassword} onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input className="form-control" type="password" value={pwForm.confirmPassword} onChange={e => setPwForm(p => ({ ...p, confirmPassword: e.target.value }))} />
                </div>
                <button className="btn btn-navy" onClick={handlePwChange} disabled={pwSaving}>
                  {pwSaving ? 'Saving…' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
